import {
  getContractManagementContract,
  updateContractManagementContract,
  deleteContractManagementContract,
  searchContractManagementContracts,
  addMatterToContract,
  removeMatterFromContract,
  repopulateContractManagementContract,
  convertContractToFile,
  convertContractToFolder,
  type FilterableSearchQuery,
  type ContractManagementContractSearchResult,
  type ContractManagementContractInDb,
  type ContractManagementContractUpdate,
  type RepopulateContractAgentTaskInDb,
  type VaultDirectoryInDb,
  type VaultFileInDb,
} from '@/api';

/**
 * Extract error message from various error types
 */
function extractErrorMessage(err: unknown, defaultMessage: string): string {
  if (err instanceof Error) {
    return err.message;
  }
  if (typeof err === 'object' && err !== null && 'detail' in err) {
    const detail = err.detail;
    return String(detail);
  }
  return defaultMessage;
}

export const ContractsService = {
  /**
   * Fetch a single contract by UID
   */
  async getById(uid: string): Promise<ContractManagementContractInDb> {
    try {
      const response = await getContractManagementContract({ path: { uid } });

      if (!response.data) {
        throw new Error('Failed to fetch contract');
      }

      return response.data;
    } catch (error) {
      throw new Error(extractErrorMessage(error, 'Failed to fetch contract. Please try again.'), {
        cause: error,
      });
    }
  },

  /**
   * Update an existing contract
   */
  async update(
    uid: string,
    updateData: ContractManagementContractUpdate
  ): Promise<ContractManagementContractInDb> {
    try {
      const response = await updateContractManagementContract({
        path: { uid },
        body: updateData,
      });

      if (!response.data) {
        throw new Error('Failed to update contract');
      }

      return response.data;
    } catch (error) {
      throw new Error(extractErrorMessage(error, 'Failed to update contract. Please try again.'), {
        cause: error,
      });
    }
  },

  /**
   * Turn a contract with exactly one file back into a plain file, in the
   * contract's folder. Needs ADMIN on the contract.
   */
  async convertToFile(uid: string): Promise<VaultFileInDb> {
    try {
      const response = await convertContractToFile({ path: { uid } });
      if (!response.data) throw new Error('Failed to convert the contract to a file');
      return response.data;
    } catch (error) {
      throw new Error(extractErrorMessage(error, 'Failed to convert the contract to a file.'), {
        cause: error,
      });
    }
  },

  /**
   * Turn a contract into a folder named after it, holding its files as plain
   * files. Needs ADMIN on the contract.
   */
  async convertToFolder(uid: string): Promise<VaultDirectoryInDb> {
    try {
      const response = await convertContractToFolder({ path: { uid } });
      if (!response.data) throw new Error('Failed to convert the contract to a folder');
      return response.data;
    } catch (error) {
      throw new Error(extractErrorMessage(error, 'Failed to convert the contract to a folder.'), {
        cause: error,
      });
    }
  },

  /**
   * Delete a contract
   * @returns The deleted contract
   */
  async delete(uid: string): Promise<ContractManagementContractInDb> {
    try {
      const response = await deleteContractManagementContract({
        path: { uid },
      });

      if (!response.data) {
        throw new Error('Failed to delete contract - no data returned from API');
      }

      return response.data;
    } catch (error) {
      throw new Error(extractErrorMessage(error, 'Failed to delete contract. Please try again.'), {
        cause: error,
      });
    }
  },

  /**
   * Start a background task that repopulates a contract's details from its files.
   * Only one repopulate task may be active per contract; a second request while one
   * is pending/running is rejected by the backend, whose message lives on
   * `response.error` (the generated client does not throw for HTTP errors).
   */
  async repopulate(uid: string): Promise<RepopulateContractAgentTaskInDb> {
    const response = await repopulateContractManagementContract({ path: { uid } });

    if (!response.data) {
      throw new Error(extractErrorMessage(response.error, 'Failed to start repopulation.'));
    }

    return response.data;
  },

  /**
   * Search all contracts with pagination and filtering
   * @param excludeMatterUids - Optional list of matter UIDs. Contracts already linked to any of these matters will be excluded from results.
   */
  async searchContracts(
    query: {
      text?: string;
      page?: number;
      pageSize?: number;
      sortKey?: string;
      sortDirection?: 'asc' | 'desc';
      columnFilters?: Record<string, string[]>;
      excludeMatterUids?: string[];
    } = {}
  ): Promise<ContractManagementContractSearchResult> {
    try {
      const searchQuery: FilterableSearchQuery = {
        text: query.text ?? '',
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- 0 must use the fallback, not be preserved
        limit: query.pageSize || 10,
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- 0 must use the fallback, not be preserved
        offset: query.page ? (query.page - 1) * (query.pageSize || 10) : 0,
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must use the fallback
        sort_key: query.sortKey || 'created_at',
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must use the fallback
        sort_direction: query.sortDirection || 'desc',
        similarity_threshold: 0.5,
        column_filters: query.columnFilters ?? {},
      };

      const response = await searchContractManagementContracts({
        body: searchQuery,
        query:
          query.excludeMatterUids && query.excludeMatterUids.length > 0
            ? {
                exclude_matter_uids: query.excludeMatterUids,
              }
            : undefined,
      });

      if (!response.data) {
        throw new Error('Failed to search contracts');
      }

      return response.data;
    } catch (error) {
      throw new Error(extractErrorMessage(error, 'Failed to search contracts. Please try again.'), {
        cause: error,
      });
    }
  },

  /**
   * Add a matter to a contract (links contract to matter)
   * @param matterUid - The UID of the matter
   * @param contractUid - The UID of the contract to add to the matter
   */
  async addMatterToContract(matterUid: string, contractUid: string): Promise<void> {
    try {
      await addMatterToContract({
        query: {
          matter_uid: matterUid,
          contract_uid: contractUid,
        },
      });
    } catch (error) {
      throw new Error(
        extractErrorMessage(error, 'Failed to add contract to matter. Please try again.'),
        { cause: error }
      );
    }
  },

  /**
   * Remove a matter from a contract (unlinks contract from matter)
   * @param matterUid - The UID of the matter
   * @param contractUid - The UID of the contract to remove from the matter
   */
  async removeMatterFromContract(matterUid: string, contractUid: string): Promise<void> {
    try {
      await removeMatterFromContract({
        query: {
          matter_uid: matterUid,
          contract_uid: contractUid,
        },
      });
    } catch (error) {
      throw new Error(
        extractErrorMessage(error, 'Failed to remove contract from matter. Please try again.'),
        { cause: error }
      );
    }
  },
};
